import { Map, Set } from 'immutable';

import { getCategoryName, getParentCategory } from '../taxonomy/categories';
import { getName, getTopTaxonomyId } from '../taxonomy';
import { sortCaseIgnore } from '../array';
import { isVelaStatus } from './statuses';
import { shapeNumber } from './listings';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';

import { CHANNEL_FILTERS, FILTER, FILTER_API_KEY, FILTER_NAME, GRADES, MAX_FILTER_ITEMS, STATUS } from '../../constants/listings';
import { PROFILE_TYPE, PROFILE_TYPES } from '../../constants/profiles';
import { EMPTY, FALSE, TRUE, VELA } from '../../constants';
import { FEATURE } from '../../constants/features';
import { AMOUNT } from '../../constants/product';
import { COLOR } from '../../constants/theme';

export function getDefaultFilters({ channel, filters = EMPTY.MAP }) {
  function reduceChannelFiltersToExpanded(expanded, filter) {
    return filters.hasIn(['expanded', filter])
      ? expanded.add(filter)
      : expanded;
  }

  return Map({
    expanded: CHANNEL_FILTERS[channel].reduce(reduceChannelFiltersToExpanded, EMPTY.SET),
    order: EMPTY.MAP,
    selected: EMPTY.MAP,
  });
}

export function shapeFiltersForApp({ channel, filters, response, shopData, status, taxonomyData, userFeatures }) {
  function shapeValues({ filter, type, values = {}}) {
    function addValues(result, item) {
      const { id, ...rest } = item;

      return result
        .update('byId', function updateById(byId) {
          return byId.set(id, Map(rest));
        })
        .update('ids', function updateIds(ids) {
          return ids.push(id);
        });
    }

    function getValues(result, count, value) {
      result.push({ count: shapeNumber(count), id: value, name: value, value });

      return result;
    }

    function sortAlphabetically(x, y) {
      return sortCaseIgnore(x.name, y.name);
    }

    function sortByCount(x, y) {
      return x.count === y.count
        ? sortCaseIgnore(x.name, y.name)
        : y.count - x.count;
    }

    function reduceCategories(result, count, category) {
      const id = getTopTaxonomyId({ category, channel });

      if (id === 0) return result;

      const index = result.findIndex(function findSame(item) {
        return item.id === id;
      });

      if (index === -1) {
        const name = getName({ category: id, channel });
        const value = Set([category]);
        result.push({ count: shapeNumber(count), id, name, value });
      } else {
        result[index].count += shapeNumber(count);
        result[index].value = result[index].value.add(category);
      }

      return result;
    }

    function reduceItemCategories(result, count, category) {
      const parent = getParentCategory({ category, taxonomyData });

      if (!parent) return result;

      const index = result.findIndex(function findSame(item) {
        return item.id === parent;
      });

      if (index === -1) {
        const name = getCategoryName({ category: parent, taxonomyData });
        const value = Set([category]);
        result.push({ count: shapeNumber(count), id: parent, name, value });
      } else {
        result[index].count += shapeNumber(count);
        result[index].value = result[index].value.add(category);
      }

      return result;
    }

    function reduceCollections(result, count, id) {
      const name = shopData.getIn(['collections', 'byId', id, 'label']);

      if (name) {
        result.push({ count: shapeNumber(count), id, name, value: id });
      }

      return result;
    }

    function reduceGrades(result, grade) {
      result.push({ count: shapeNumber(values[grade]), id: grade, value: grade });
      return result;
    }

    function reduceProfiles(result, count, id) {
      const path = ['profiles', type, 'byId', id];

      if (!shopData.hasIn(path)) return result;

      const name = shopData.getIn([...path, 'name']);

      result.push({ count: shapeNumber(count), id, name, value: id });

      return result;
    }

    function reduceSections(result, count, id) {
      const name = shopData.getIn(['sections', 'byId', id]);

      if (name) {
        result.push({ count: shapeNumber(count), id, name, value: id });
      }

      return result;
    }

    function reduceStoreCategories(result, count, category) {
      const name = shopData.getIn(['storeCategories', 'byId', category]);

      if (name) {
        result.push({ count: shapeNumber(count), id: category, name, value: category });
      }

      return result;
    }

    const result = Map({ byId: EMPTY.MAP, ids: EMPTY.LIST });

    switch (filter) {
      case FILTER.CATEGORY: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], reduceCategories).sort(sortByCount).reduce(addValues, result);
      }

      case FILTER.COLLECTIONS: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], reduceCollections).sort(sortByCount).reduce(addValues, result);
      }

      case FILTER.EXISTING: {
        const options = [
          {
            count: values ? shapeNumber(values[FALSE]) : 0,
            id: FALSE,
            name: 'New listings',
            value: FALSE,
          },
          {
            count: values ? shapeNumber(values[TRUE]) : 0,
            id: TRUE,
            name: 'Existing listings',
            value: TRUE,
          },
        ];

        return options.reduce(addValues, Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }));
      }

      case FILTER.IS_COMPLETE: {
        const options = [
          {
            count: shapeNumber(values[TRUE]),
            id: TRUE,
            indicator: COLOR.CLOVER,
            name: 'Complete',
            value: TRUE,
          },
          {
            count: shapeNumber(values[FALSE]),
            id: FALSE,
            indicator: COLOR.ROUGE,
            name: 'Incomplete',
            value: FALSE,
          },
        ];

        return options.reduce(addValues, Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }));
      }

      case FILTER.ITEM_CATEGORY: {
        return reduce(values, [], reduceItemCategories).sort(sortByCount).reduce(addValues, result);
      }

      case FILTER.PROFILE: {
        if (getSize(values) === 0) return result;

        const { [AMOUNT.MINUS_TWO]: count, ...rest } = values;

        if (getSize(rest) === 0) return result;

        const options = reduce(rest, [], reduceProfiles).sort(sortByCount);

        if (getSize(options) === 0) return result;

        if (shapeNumber(count)) {
          options.push(
            {
              count: shapeNumber(count),
              id: AMOUNT.MINUS_TWO,
              name: `No ${FILTER_NAME[filter]}`,
              value: AMOUNT.MINUS_TWO,
            },
          );
        }

        return options.reduce(addValues, result);
      }

      case FILTER.SCORE: {
        return GRADES.reduce(reduceGrades, []).reduce(addValues, result);
      }

      case FILTER.SECTION: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], reduceSections).sort(sortByCount).reduce(addValues, result);
      }

      case FILTER.STORE_CATEGORY: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], reduceStoreCategories).sort(sortByCount).reduce(addValues, result);
      }

      case FILTER.TAGS: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], getValues).sort(sortAlphabetically).reduce(addValues, result);
      }

      case FILTER.TAXONOMY: {
        return reduce(values, [], reduceCategories).sort(sortByCount).reduce(addValues, result);
      }

      default: {
        if (getSize(values) === 0) return result;

        return reduce(values, [], getValues).sort(sortByCount).reduce(addValues, result);
      }
    }
  }

  function reduceChannelFilters(source, filter) {
    let result = source;

    function updateOptions(options) {
      return options.push(filter);
    }

    switch (filter) {
      case FILTER.EXISTING: {
        if ((status !== STATUS[VELA].IMPORTED && status !== STATUS[VELA].UNPUBLISHED)) break;

        result = result.setIn(['values', filter], shapeValues({ filter, values: response[FILTER_API_KEY[filter]] }));

        break;
      }

      case FILTER.IS_COMPLETE: {
        if (!isVelaStatus({ channel, status })) break;

        result = result
          .setIn(['values', filter], shapeValues({ filter, values: response[FILTER_API_KEY[filter]] }))
          .update('options', updateOptions);

        break;
      }

      case FILTER.PROFILE: {
        let values = EMPTY.MAP;

        if (!userFeatures.get(FEATURE.AI_PROFILES)) {
          values = shapeValues({
            filter: FILTER.PROFILE,
            type: PROFILE_TYPE.STANDARD,
            values: response[FILTER_API_KEY[FILTER.PROFILE][PROFILE_TYPE.STANDARD]],
          });

          if (getSize(values.get('ids')) === 0) {
            return result.deleteIn(['expanded', filter]);
          }

          result = result
            .setIn(['values', filter], values)
            .update('options', updateOptions);

          if (result.getIn(['selected', FILTER.PROFILE_TYPE])) {
            result = result
              .updateIn(['selected', FILTER.PROFILE_TYPE], function updateType(type) {
                if (!type) return undefined;

                function predicate(profileId) {
                  return (
                    profileId !== AMOUNT.MINUS_TWO &&
                    shopData.getIn(['profiles', PROFILE_TYPE.STANDARD, 'byId', profileId, 'sections', type])
                  );
                }

                if (values.get('ids').some(predicate)) {
                  return type;
                }

                return undefined;
              });
          }

          if (result.getIn(['expanded', filter])) {
            if (result.getIn(['selected', FILTER.PROFILE_TYPE])) {
              const type = result.getIn(['selected', FILTER.PROFILE_TYPE]);
              const optionsSize = getSize(values.get('ids').filter(function filterProfiles(profileId) {
                return (
                  profileId === AMOUNT.MINUS_TWO ||
                  shopData.getIn(['profiles', PROFILE_TYPE.STANDARD, 'byId', profileId, 'sections', type])
                );
              }));

              if (optionsSize <= MAX_FILTER_ITEMS) {
                result = result.update('expanded', function updateExpanded(expanded = EMPTY.SET) {
                  return expanded.delete(filter);
                });
              }
            } else {
              const optionsSize = getSize(values.get('ids'));

              if (optionsSize <= MAX_FILTER_ITEMS) {
                result = result.update('expanded', function updateExpanded(expanded = EMPTY.SET) {
                  return expanded.delete(filter);
                });
              }
            }
          }

          break;
        }

        for (const type of PROFILE_TYPES) {
          const shaped = shapeValues({
            filter: FILTER.PROFILE,
            type,
            values: response[FILTER_API_KEY[FILTER.PROFILE][type]],
          });

          if (getSize(shaped.get('ids')) === 0) {
            continue;
          }

          values = values.set(type, shaped);
        }

        if (getSize(values) === 0) {
          return result
            .deleteIn(['expanded', filter])
            .deleteIn(['selected', FILTER.PROFILE_TYPE]);
        }

        result = result
          .setIn(['values', filter], values)
          .update('options', updateOptions)
          .updateIn(['selected', FILTER.PROFILE_TYPE], function updateType(type) {
            function exists(item) {
              return values.has(item);
            }

            return type && exists(type)
              ? type
              : PROFILE_TYPES.find(exists);
          });

        result = result
          .update('expanded', function updateExpanded(expanded = EMPTY.SET) {
            return (
              expanded.has(filter) &&
              getSize(values.getIn([result.getIn(['selected', FILTER.PROFILE_TYPE]), 'ids'])) <= MAX_FILTER_ITEMS
            )
              ? expanded.delete(filter)
              : expanded;
          });

        break;
      }

      case FILTER.SCORE: {
        if (!userFeatures.get(FEATURE.LISTING_SCORE)) break;

        if (!response.hasOwnProperty(FILTER_API_KEY[filter])) break;

        result = result
          .setIn(['values', filter], shapeValues({ filter, values: response[FILTER_API_KEY[filter]] }))
          .update('options', updateOptions);

        break;
      }

      default: {
        const values = shapeValues({ filter, values: response[FILTER_API_KEY[filter]] });
        const size = getSize(values.get('ids'));

        if (!size) return result;

        result = result
          .setIn(['values', filter], values)
          .update('expanded', function updateExpanded(expanded = EMPTY.SET) {
            return expanded.has(filter) && size <= MAX_FILTER_ITEMS
              ? expanded.delete(filter)
              : expanded;
          })
          .update('options', updateOptions);

        break;
      }
    }

    return result;
  }

  return CHANNEL_FILTERS[channel].reduce(
    reduceChannelFilters,
    filters.set('options', EMPTY.LIST).set('values', EMPTY.MAP),
  );
}

export function shapeFiltersForAPI({ channel, filters, status, userFeatures }) {
  function reduceFilters(result, value, key) {
    switch (key) {
      case FILTER.EXISTING: {
        if ((status === STATUS[VELA].IMPORTED || status === STATUS[VELA].UNPUBLISHED)) {
          result[FILTER_API_KEY[key]] = value;
        }

        break;
      }

      case FILTER.IS_COMPLETE: {
        if (isVelaStatus({ channel, status }) && getSize(value) === 1) {
          result[FILTER_API_KEY[key]] = value.toList().get(0);
        }

        break;
      }

      case FILTER.PROFILE: {
        if (!userFeatures.get(FEATURE.AI_PROFILES)) {
          if (getSize(value) > 0) {
            result[FILTER_API_KEY[key][PROFILE_TYPE.STANDARD]] = value.toArray();
          }

          break;
        }

        const type = filters.get(FILTER.PROFILE_TYPE);

        if (type && getSize(value) > 0) {
          result[FILTER_API_KEY[key][type]] = value.toArray();
        }

        break;
      }

      case FILTER.PROFILE_TYPE: {
        break;
      }

      case FILTER.SCORE: {
        if (!userFeatures.get(FEATURE.LISTING_SCORE)) break;

        if (getSize(value) > 0) {
          result[FILTER_API_KEY[FILTER.SCORE]] = value.toArray();
        }

        break;
      }

      case FILTER.TITLE: {
        if (getSize(value) > 0) {
          result[FILTER_API_KEY[key]] = value;
        }

        break;
      }

      default: {
        if (getSize(value) > 0) {
          result[FILTER_API_KEY[key]] = value.toArray();
        }

        break;
      }
    }

    return result;
  }

  const initial = {
    [FILTER_API_KEY[FILTER.STATUS]]: status,
  };

  return filters.reduce(reduceFilters, initial);
}

export function onlyComplete(filters = EMPTY.MAP) {
  return (
    filters.hasIn([FILTER.IS_COMPLETE, TRUE]) &&
    !filters.hasIn([FILTER.IS_COMPLETE, FALSE])
  );
}
